import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Megaphone, Users as UsersIcon, Send, Clock, CheckCircle2 } from "lucide-react";
import PrivateLayout from "../components/PrivateLayout";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError } from "../services/api";

const TARGETS = [
  { key: "all",           label: "Tous les utilisateurs",  Icon: UsersIcon },
  { key: "talent",        label: "Tous les Talents",        Icon: UsersIcon },
  { key: "employeur",     label: "Toutes les Entreprises",  Icon: UsersIcon },
  { key: "universite",    label: "Toutes les Universités",  Icon: UsersIcon },
  { key: "investisseur",  label: "Tous les Investisseurs",  Icon: UsersIcon },
  { key: "entrepreneur",  label: "Tous les Bâtisseurs",     Icon: UsersIcon },
  { key: "freelance",     label: "Tous les Freelances",     Icon: UsersIcon },
  { key: "coach",         label: "Tous les Coachs",         Icon: UsersIcon },
];

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}

export default function AdminBroadcast() {
  const { user } = useAuth();
  const [target, setTarget] = useState("all");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState([]);

  const isSuperAdmin = ["super-admin", "admin"].includes(user?.role);

  const loadHistory = async () => {
    try {
      const { data } = await api.get("/v1/admin/broadcast/history");
      setHistory(data.items || []);
    } catch { /* silent */ }
  };

  useEffect(() => { if (isSuperAdmin) loadHistory(); }, [isSuperAdmin]);

  const send = async (e) => {
    e.preventDefault();
    if (!body.trim()) { toast.error("Le message ne peut pas être vide"); return; }
    if (!window.confirm(`Envoyer ce message à « ${TARGETS.find((t) => t.key === target).label} » ?`)) return;
    setSending(true);
    try {
      const { data } = await api.post("/v1/admin/broadcast", { target_role: target, body: body.trim(), subject: subject.trim() || null });
      toast.success(`Diffusion terminée : ${data.delivered} envoyé(s)${data.failed ? `, ${data.failed} échec(s)` : ""}`);
      setBody(""); setSubject("");
      await loadHistory();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  if (!isSuperAdmin) {
    return (
      <PrivateLayout title="Diffusion" breadcrumbs={[{ label: "Diffusion" }]}>
        <div className="afr-empty" data-testid="broadcast-forbidden">
          <div className="afr-empty__title">Réservé aux administrateurs</div>
        </div>
      </PrivateLayout>
    );
  }

  return (
    <PrivateLayout
      title="Diffusion de messages"
      breadcrumbs={[{ to: "/admin", label: "Admin" }, { label: "Diffusion" }]}
    >
      <form onSubmit={send} className="afr-card" style={{ padding: "1.5rem", maxWidth: 780 }} data-testid="broadcast-form">
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
          <div style={{ width: 44, height: 44, borderRadius: 12, background: "var(--afr-primary,#009640)", color: "#fff", display: "grid", placeItems: "center" }}>
            <Megaphone size={20} />
          </div>
          <div>
            <strong style={{ fontSize: "1.05rem" }}>Envoyer un message groupé</strong>
            <div className="afr-muted" style={{ fontSize: "0.85rem" }}>
              Une conversation est créée (ou réutilisée) avec chaque destinataire — aucun doublon.
            </div>
          </div>
        </div>

        <label style={{ display: "block", fontSize: 12, color: "#64748b", marginBottom: 6, fontWeight: 700, textTransform: "uppercase" }}>
          Audience
        </label>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 16 }} data-testid="broadcast-targets">
          {TARGETS.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTarget(t.key)}
              data-testid={`broadcast-target-${t.key}`}
              className={`afr-btn afr-btn--sm ${target === t.key ? "afr-btn--primary" : "afr-btn--outline"}`}
            >
              <t.Icon size={12} /> {t.label}
            </button>
          ))}
        </div>

        <label style={{ display: "block", fontSize: 12, color: "#64748b", marginBottom: 4 }}>Sujet (optionnel)</label>
        <input className="afr-input" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Ex : Nouveauté AFRICTAL de la semaine" data-testid="broadcast-subject" />

        <label style={{ display: "block", fontSize: 12, color: "#64748b", marginBottom: 4, marginTop: 12 }}>Message</label>
        <textarea
          className="afr-input"
          rows={6}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Rédigez le message qui sera envoyé à chaque destinataire…"
          data-testid="broadcast-body"
          required
        />
        <div className="afr-muted" style={{ fontSize: "0.78rem", marginTop: 6 }}>
          {body.length}/4000 caractères
        </div>

        <button
          type="submit"
          className="afr-btn afr-btn--primary"
          disabled={sending || !body.trim()}
          data-testid="broadcast-send"
          style={{ marginTop: 16 }}
        >
          {sending ? <><Clock size={14} /> Envoi en cours…</> : <><Send size={14} /> Diffuser le message</>}
        </button>
      </form>

      <section style={{ marginTop: "2rem", maxWidth: 780 }} data-testid="broadcast-history">
        <h3 style={{ fontFamily: "'Outfit',sans-serif", color: "var(--afr-secondary)", marginBottom: 12 }}>
          Historique des diffusions
        </h3>
        {history.length === 0 && <div className="afr-muted" style={{ fontSize: "0.9rem" }}>Aucune diffusion pour le moment.</div>}
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {history.map((h) => (
            <article key={h.id} className="afr-card" style={{ padding: 12 }} data-testid={`broadcast-history-${h.id}`}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                <div>
                  <strong style={{ fontSize: "0.92rem" }}>{TARGETS.find((t) => t.key === h.target_role)?.label || h.target_role}</strong>
                  <div className="afr-muted" style={{ fontSize: "0.78rem" }}>{fmtDate(h.started_at)} · par {h.sender_name || "—"}</div>
                </div>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 10px", borderRadius: 999, background: "rgba(21,128,61,0.10)", color: "#15803D", fontSize: 12, fontWeight: 700 }}>
                  <CheckCircle2 size={12} /> {h.delivered || 0} livré(s)
                  {h.failed > 0 ? ` · ${h.failed} échec(s)` : ""}
                </span>
              </div>
              {h.subject && <div style={{ fontSize: "0.85rem", marginTop: 6, fontWeight: 600 }}>{h.subject}</div>}
              <div style={{ fontSize: "0.86rem", color: "#475569", marginTop: 4, whiteSpace: "pre-wrap" }}>{h.body}</div>
            </article>
          ))}
        </div>
      </section>
    </PrivateLayout>
  );
}
